import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import { parsePesoToCentavos } from "@/lib/money";

export default function PaymentAllocationSummary({
  rows = [],
  currency,
  fmtPeso,
  paymentState,
  setPaymentState,
  setPaymentField,
  setAllocationOverride,
  allocationCreditCents = 0,
  managerApprovalRequired = false,
  hasPlanBucket = false,
  renderAmountInput = null,
}) {
  const forceExpanded = !!paymentState?.allocation_overrides_enabled
    || managerApprovalRequired
    || allocationCreditCents > 0;
  const [expanded, setExpanded] = useState(forceExpanded);

  useEffect(() => {
    if (forceExpanded) setExpanded(true);
  }, [forceExpanded]);

  const totalAllocatedCents = rows.reduce((sum, row) => sum + Number(row.amount_centavos || 0), 0);
  const previewRows = useMemo(() => rows.slice(0, 2), [rows]);
  const overflowCount = Math.max(0, rows.length - previewRows.length);

  if (!rows.length) return null;

  function toggleOverrides(checked) {
    setPaymentState((prev) => ({
      ...prev,
      allocation_overrides_enabled: checked,
      procedure_allocation_overrides: checked
        ? rows.map((row) => ({
            item_index: row.item_index,
            amount_centavos: row.amount_centavos,
          }))
        : [],
    }));
  }

  const detailPanel = expanded ? (
        <div className="border-t border-slate-200 bg-slate-50 px-4 py-3 space-y-3" data-testid="payment-allocation-preview">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-800">Payment application</p>
              <p className="text-xs text-slate-500">
                {hasPlanBucket
                  ? "Payment applies to today’s bill first. Tx Plan work is recorded as completed; plan balances follow the billing model."
                  : "Single-visit work closes first; any current-visit surplus becomes patient credit."}
              </p>
            </div>
            <label className="inline-flex shrink-0 items-center gap-2 text-xs font-medium text-slate-700">
              <input
                type="checkbox"
                checked={!!paymentState?.allocation_overrides_enabled}
                onChange={(e) => toggleOverrides(e.target.checked)}
                data-testid="allocation-override-toggle"
                className="h-3.5 w-3.5 rounded border-slate-300 text-teal-600"
              />
              Change payment application
            </label>
          </div>

          <div className="space-y-2">
            {rows.map((row) => (
              <div key={row.key} className="grid grid-cols-[1fr_auto] items-center gap-3 rounded-lg border border-slate-100 bg-white px-3 py-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-800">{row.label}</p>
                  <p className="text-xs text-slate-500">
                    {row.detail} · Due {fmtPeso(row.due_centavos, currency)}
                  </p>
                </div>
                {paymentState?.allocation_overrides_enabled ? (
                  renderAmountInput
                    ? renderAmountInput(row)
                    : (
                      <input
                        type="number"
                        value={Number(row.amount_centavos || 0) / 100}
                        onChange={(e) => setAllocationOverride?.(row, parsePesoToCentavos(e.target.value))}
                        className="w-28 rounded-lg border border-slate-200 px-2 py-1 text-right text-sm"
                      />
                    )
                ) : (
                  <p className="text-sm font-semibold tabular-nums text-slate-900">
                    {fmtPeso(row.amount_centavos, currency)}
                  </p>
                )}
              </div>
            ))}
          </div>

          {allocationCreditCents > 0 ? (
            <div className="flex items-center justify-between rounded-lg border border-teal-100 bg-teal-50 px-3 py-2">
              <span className="text-sm text-teal-800">Patient credit</span>
              <span className="text-sm font-semibold tabular-nums text-teal-900">
                {fmtPeso(allocationCreditCents, currency)}
              </span>
            </div>
          ) : null}

          {managerApprovalRequired ? (
            <div
              className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 space-y-2"
              data-testid="allocation-manager-approval"
            >
              <div className="flex items-start gap-2 text-amber-900">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <p className="text-xs font-medium">
                  Manager approval is required because a single-visit procedure is collecting zero.
                </p>
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                <input
                  type="text"
                  value={paymentState?.manager_approval_by || ""}
                  onChange={(e) => setPaymentField("manager_approval_by", e.target.value)}
                  placeholder="Manager"
                  data-testid="allocation-manager-by"
                  className="w-full rounded-lg border border-amber-200 bg-white px-3 py-2 text-sm outline-none focus:border-amber-500"
                />
                <input
                  type="text"
                  value={paymentState?.manager_approval_reason || ""}
                  onChange={(e) => setPaymentField("manager_approval_reason", e.target.value)}
                  placeholder="Reason"
                  data-testid="allocation-manager-reason"
                  className="w-full rounded-lg border border-amber-200 bg-white px-3 py-2 text-sm outline-none focus:border-amber-500"
                />
              </div>
            </div>
          ) : null}
        </div>
  ) : null;

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-white shadow-sm"
      data-testid="payment-allocation-summary"
    >
      <div className="px-4 py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900">
            Payment covers <span className="text-slate-500">{rows.length} procedure{rows.length === 1 ? "" : "s"}</span>
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-600">
            {previewRows.map((row) => (
              <span key={row.key} className="max-w-full truncate">
                {row.label} <span className="font-semibold tabular-nums text-slate-800">{fmtPeso(row.amount_centavos, currency)}</span>
              </span>
            ))}
            {overflowCount > 0 ? (
              <span className="text-slate-500">+ {overflowCount} more</span>
            ) : null}
            {allocationCreditCents > 0 ? (
              <span className="font-semibold text-teal-700">
                Patient credit {fmtPeso(allocationCreditCents, currency)}
              </span>
            ) : null}
          </div>
        </div>
        <div className="flex shrink-0 items-center justify-between gap-3 sm:justify-end">
          <span className="text-sm font-semibold tabular-nums text-slate-950">
            {fmtPeso(totalAllocatedCents, currency)}
          </span>
          <button
            type="button"
            onClick={() => setExpanded((open) => !open)}
            data-testid="payment-allocation-change"
            aria-expanded={expanded}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            {expanded ? "Hide" : "Change"}
            {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>
      {detailPanel}
    </section>
  );
}
